import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import { Box, Button, IconButton, InputAdornment, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { DataGrid } from '@mui/x-data-grid';
import { post } from 'api';
import { download } from 'api';
import { url } from 'config/config';
import { enqueueSnackbar } from 'notistack';
import StockUpdateDialog from 'components/SS/StockUpdateForm';
import VendorCreate from 'components/SS/vendorCreateForm';
import IconifyIcon from 'components/base/IconifyIcon';
import Image from 'components/base/Image';
import DataGridPagination from 'components/pagination/DataGridPagination';
import StyledTextField from 'components/styled/StyledTextField';

const EditButton = ({ row }) => {
  const navigate = useNavigate();
  const handleView = () => {
    navigate('/create-inventory', { state: row });
  };

  return (
    <IconButton onClick={handleView}>
      <ModeEditOutlineOutlinedIcon />
    </IconButton>
  );
};
const columns = [
  {
    field: 'view',
    headerName: 'View',
    sortable: false,
    filterable: true,
    width: 60,
    renderCell: (params) => <EditButton row={params.row} />,
  },
  {
    field: 'image',
    headerName: 'Image',
    width: 70,
    renderCell: (params) => {
      const { productImage } = params.row;
      // <Avatar src={params.value} alt="product" variant="rounded" sx={{ width: 56, height: 56 }} />
      return (
        <Stack
          spacing={1.25}
          sx={{
            alignItems: 'center',
          }}
        >
          <Image
            src={`${url}${productImage}`}
            alt="product"
            // onClick={() => navigate(paths.productDetails(String(params.row.id)))}
            sx={{ cursor: 'pointer' }}
            height={48}
            width={48}
          />
        </Stack>
      );
    },
  },
  { field: 'productName', headerName: 'Product Name', width: 240 },
  { field: 'hsnCode', headerName: 'HSN Code', width: 90 },
  { field: 'minimumQnt', headerName: 'Min Qty', width: 80 },
  { field: 'currentQnt', headerName: 'Stock', width: 80 },
  { field: 'unitType', headerName: 'Unit', width: 100 },
  { field: 'manufactureName', headerName: 'Manufacturer', width: 210 },
  {
    field: 'vendorName',
    headerName: 'Vendor',
    width: 170,
    renderCell: (params) => {
      const { vendorName } = params.row.vendorName;
      return (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
            {vendorName || ''}
          </Typography>
        </Box>
      );
    },
  },
];

const InventoryList = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
    search: '',
  });
  const [open, setOpen] = useState(false);
  const [vendorOpen, setVendorOpen] = useState(false);
  const [products, setProduct] = useState([]);
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, search: search }));
    }, 600);
    return () => clearTimeout(timeout);
  }, [search]);

  const fetchProductDropdown = async () => {
    const response = await post('users/dropdown_product', {});
    if (response.status == 200) {
      setProduct(response.data);
    } else {
      enqueueSnackbar(response.message || 'Error while Fetching Products dropdown.', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await post('users/dropdown_vendor', {});
      if (response.status == 200) {
        setVendors(response.data);
      } else {
        enqueueSnackbar(response.message || 'Error while Fetching Vendor dropdown.', {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  useEffect(() => {
    fetchProductDropdown();
    fetchVendors();
  }, []);

  const fetchData = async () => {
    try {
      const response = await post('users/list_products', {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: paginationModel.search,
      });
      if (response.status == 200) {
        setData(response.data);
        setTotal(response.total);
      } else {
        enqueueSnackbar(response.message || 'Error while Fetching Products.', {
          variant: 'error',
        });
      }
    } catch (error) {
      console.log('error', error);
      enqueueSnackbar(error.message || 'Error while Fetching Products.', { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchData();
  }, [paginationModel]);

  const handleDownload = async () => {
    try {
      const res = await download('users/inventory_excel', {});

      const url = window.URL.createObjectURL(
        new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
      );

      const a = document.createElement('a');
      a.href = url;
      a.download = 'inventory.xlsx';
      a.click();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    }
  };
  return (
    <>
      <Box sx={{ p: 2, m: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          {/* <Typography variant="h5" mb={2}>
            Inventory List
          </Typography> */}
          <StyledTextField
            id="search-box"
            type="search"
            fullWidth
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <IconifyIcon icon="material-symbols:search-rounded" fontSize={20} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              maxWidth: { sm: 200, md: 240 },
              flexGrow: { xs: 1, sm: 0 },
              mb: 3,
            }}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="soft"
              shape="square"
              color="neutral"
              sx={{ gap: 0.5, p: 1 }}
              onClick={handleDownload}
            >
              <IconifyIcon icon="material-symbols:arrow-downward" />
            </Button>
            <Button variant="soft" shape="square" color="neutral" sx={{ gap: 0.5, p: 1 }}>
              <IconifyIcon icon="material-symbols:add-2-rounded" />
              <Box
                component="span"
                sx={{ display: { xs: 'none', sm: 'block' } }}
                onClick={() => setVendorOpen(!vendorOpen)}
              >
                Add Vendor
              </Box>
            </Button>

            <Button variant="soft" shape="square" color="neutral" sx={{ gap: 0.5, p: 1 }}>
              <IconifyIcon icon="material-symbols:add-2-rounded" />
              <Box
                component="span"
                sx={{ display: { xs: 'none', sm: 'block' } }}
                onClick={() => setOpen(!open)}
              >
                Add Inventory
              </Box>
            </Button>
          </Box>
        </Box>

        <DataGrid
          rows={data}
          columns={columns}
          getRowId={(row) => row._id}
          pagination
          paginationMode="server"
          rowCount={total}
          paginationModel={paginationModel} // { page: 0, pageSize: 10 }
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 20]}
          // checkboxSelection
          sx={{
            '& .margin': {
              pr: 3,
            },
            '& .MuiDataGrid-columnHeaders': {
              minWidth: '100%',
            },
          }}
          slots={{
            basePagination: (props) => <DataGridPagination showFullPagination {...props} />,
          }}
          getRowClassName={(params) =>
            params.row.currentQnt < params.row.minimumQnt ? 'low-stock-row' : ''
          }
        />
      </Box>

      {open && (
        <StockUpdateDialog
          open={open}
          setOpen={setOpen}
          onClose={() => {
            setOpen(false);
            fetchData();
          }}
          vendors={vendors}
          products={products}
          onSubmitForm={() => {}}
        />
      )}
      {vendorOpen && (
        <VendorCreate
          open={vendorOpen}
          onClose={() => {
            setVendorOpen(false);
          }}
        />
      )}
    </>
  );
};

export default InventoryList;
